import { Sparkles, Lock, ArrowRight, X, Cpu, Globe, Trophy, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";

/**
 * Auth gate that appears when an anonymous user tries to send their first message.
 * Single passwordless email + OTP flow.
 */
export function AuthGateModal({ open, onClose, pendingMessage }) {
  const navigate = useNavigate();

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const goAuth = () => {
    if (pendingMessage) {
      try { sessionStorage.setItem("sabzy_pending_message", pendingMessage); } catch { /* ignore */ }
    }
    navigate("/auth");
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-fade-in"
      data-testid="auth-gate-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-background/85 backdrop-blur-2xl" />

      <div className="relative w-full max-w-[460px] overflow-hidden rounded-2xl border border-border bg-card shadow-[0_20px_80px_-20px_rgba(0,0,0,0.6)] animate-fade-up">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-lg text-muted-foreground transition hover:bg-secondary hover:text-foreground z-10"
          data-testid="auth-gate-close"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-7 sm:p-9">
          <div className="mb-5 grid h-12 w-12 place-items-center rounded-xl bg-foreground text-background">
            <span
              className="text-xl font-semibold"
              style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.04em" }}
            >
              S
            </span>
          </div>

          <h2
            className="text-2xl font-semibold tracking-tight sm:text-[28px]"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Continue to Sabzy
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Enter your email — we'll send a 6-digit code. Your message is saved and sent right after you verify.
          </p>

          {pendingMessage && (
            <div className="mt-5 rounded-xl border border-border/60 bg-secondary/40 px-3.5 py-2.5">
              <div className="mb-1 text-[11px] font-medium text-muted-foreground">Your message</div>
              <div className="line-clamp-3 text-sm text-foreground">
                {pendingMessage}
              </div>
            </div>
          )}

          {/* Single CTA */}
          <Button
            onClick={goAuth}
            className="group mt-6 h-12 w-full justify-center rounded-xl text-sm font-medium"
            data-testid="auth-gate-email-btn"
          >
            <Mail className="mr-2 h-4 w-4" />
            Continue with email
            <ArrowRight className="ml-2 h-4 w-4 transition group-hover:translate-x-0.5" />
          </Button>
          <p className="mt-3 text-center text-[11px] text-primary" data-testid="auth-gate-trial-note">
            Every new account = 7 days of Pro Max free · no card
          </p>

          {/* Perks */}
          <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2 text-[12px] text-muted-foreground">
            {[
              { icon: Cpu, text: "ChatGPT Astra, Claude Opus 5 & Gemini Pro" },
              { icon: Globe, text: "Live web search" },
              { icon: Trophy, text: "Pro Max racing" },
              { icon: Sparkles, text: "Saved chats" },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-2">
                <Icon className="h-3.5 w-3.5 text-primary" />
                <span>{text}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
            <Lock className="h-3 w-3" />
            Passwordless · encrypted · no spam
          </div>

          <div className="mt-4 text-center">
            <button
              onClick={onClose}
              className="text-[12px] text-muted-foreground transition hover:text-foreground"
              data-testid="auth-gate-dismiss"
            >
              Continue browsing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
